import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  Card,
  CardContent,
  CardMedia,
  SelectChangeEvent,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import { callApi } from "../../../utils/apiHelper";
import { apiEndpoints } from "../../../utils/apiEndpoints";
import {
  getRawMaterials,
  getCompanyUsers,
  User,
  RawMaterial,
  UnitType,
  unitCategoryMap,
  standardConversions,
  UnitConversion,
} from "../../../utils/commonApis";
import UnitConverter from "../../UnitConverter";

// Status-to-color mapping
const statusColors = {
  Pending: "#FFA000", // Amber
  Delivered: "#2196F3", // Blue
  Approved: "#4CAF50", // Green
  Rejected: "#F44336", // Red
};

// Define data structures for verification
// Create a security-specific PurchaseOrder interface
interface PurchaseOrder {
  PurchaseOrderId: string;
  VendorId: string;
  VendorName: string;
  VendorPhone: string;
  TotalPrice: number;
  Status: string;
  CreatedAt: number;
  UpdatedAt: number;
  RawMaterials: VerificationRawMaterial[];
  HtmlUrl: string;
  CreatedBy: string;
  CompanyId: string;
}

interface VerificationRawMaterial {
  RawMaterialId: string;
  Quantity: number;
  Price: number;
  Name?: string;
  BaseUnit?: string;
  Code?: string;
  Description?: string;
  ConvertedQuantity?: number;
  ConvertedUnit?: string;
  UnitConversions?: UnitConversion[];
}

interface VerificationFormData {
  purchaseOrderId: string;
  rawMaterials: VerificationRawMaterial[];
  vendorBillId: string;
  vendorBillImages: string[];
  securityComments: string;
  assignedTo: string;
}

const PurchaseOrderVerification: React.FC = () => {
  // State for purchase order
  const [searchPurchaseOrderId, setSearchPurchaseOrderId] = useState<string>("");
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // State for verification form
  const [verificationForm, setVerificationForm] = useState<VerificationFormData>({
    purchaseOrderId: "",
    rawMaterials: [],
    vendorBillId: "",
    vendorBillImages: [],
    securityComments: "",
    assignedTo: "",
  });

  // State for raw materials and warehouse users
  const [availableRawMaterials, setAvailableRawMaterials] = useState<RawMaterial[]>([]);
  const [warehouseUsers, setWarehouseUsers] = useState<User[]>([]);

  // State for image preview
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State for the material being edited
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<number | null>(null);
  const [materialDetailsDialogOpen, setMaterialDetailsDialogOpen] = useState<boolean>(false);

  // Fetch raw materials and warehouse users on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch raw materials
        const rawMaterialsResponse = await getRawMaterials();
        if (rawMaterialsResponse && rawMaterialsResponse.rawMaterials) {
          setAvailableRawMaterials(rawMaterialsResponse.rawMaterials);
        }

        // Fetch warehouse users (role 4)
        const warehouseUsersData = await getCompanyUsers(4);
        if (Array.isArray(warehouseUsersData)) {
          setWarehouseUsers(warehouseUsersData);
        } else if (warehouseUsersData && warehouseUsersData.users) {
          setWarehouseUsers(warehouseUsersData.users);
        }
      } catch (err) {
        setError("Failed to fetch initial data: " + (err instanceof Error ? err.message : "Unknown error"));
      }
    };

    fetchInitialData();
  }, []);

  // Search for a purchase order by ID
  const searchPurchaseOrder = async () => {
    if (!searchPurchaseOrderId.trim()) {
      setError("Please enter a Purchase Order ID");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await callApi<any>(
        apiEndpoints.NulookErpProcessPurchaseOrder,
        "POST",
        {
          operation: "get_purchase_order",
          PurchaseOrderId: searchPurchaseOrderId.trim(),
        }
      );

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (response.data) {
        // Convert response data to a strongly typed object
        const poData: any = response.data;
        
        // Enrich raw materials with names and unit information
        const poRawMaterials = Array.isArray(poData.RawMaterials) ? poData.RawMaterials : [];
        const enrichedMaterials = poRawMaterials.map((material: VerificationRawMaterial) => {
          const rawMaterial = availableRawMaterials.find((rm: RawMaterial) => rm.RawMaterialId === material.RawMaterialId);
          return {
            ...material,
            Name: rawMaterial?.Name || "Unknown Material",
            BaseUnit: rawMaterial?.BaseUnit || "Pcs",
            Code: rawMaterial?.Code || "",
            Description: rawMaterial?.Description || "",
            UnitConversions: rawMaterial?.UnitConversions || [],
            ConvertedQuantity: material.Quantity,
            ConvertedUnit: rawMaterial?.BaseUnit || "Pcs",
          };
        });

        const purchaseOrder: PurchaseOrder = {
          PurchaseOrderId: poData.PurchaseOrderId || "",
          VendorId: poData.VendorId || "",
          VendorName: poData.VendorName || "",
          VendorPhone: poData.VendorPhone || "",
          TotalPrice: poData.TotalPrice || 0,
          Status: poData.Status || "Pending",
          CreatedAt: poData.CreatedAt || 0,
          UpdatedAt: poData.UpdatedAt || 0,
          RawMaterials: enrichedMaterials,
          HtmlUrl: poData.HtmlUrl || "",
          CreatedBy: poData.CreatedBy || "",
          CompanyId: poData.CompanyId || "",
        };

        setSelectedPurchaseOrder(purchaseOrder);
        
        // Initialize verification form
        setVerificationForm({
          purchaseOrderId: poData.PurchaseOrderId || "",
          rawMaterials: enrichedMaterials,
          vendorBillId: "",
          vendorBillImages: [],
          securityComments: "",
          assignedTo: warehouseUsers.length > 0 ? warehouseUsers[0].UserId : "",
        });

        // Reset preview images
        setPreviewImages([]);
      } else {
        throw new Error("No purchase order found with the provided ID");
      }
    } catch (err) {
      setError("Failed to fetch purchase order details: " + (err instanceof Error ? err.message : "Unknown error"));
      setSelectedPurchaseOrder(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input changes
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPurchaseOrderId(e.target.value);
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setVerificationForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle assigned user change
  const handleAssignedToChange = (e: SelectChangeEvent) => {
    setVerificationForm(prev => ({ ...prev, assignedTo: e.target.value }));
  };

  // Handle material quantity change
  const handleMaterialChange = (index: number, value: number, unit: string) => {
    const updatedMaterials = [...verificationForm.rawMaterials];
    updatedMaterials[index] = {
      ...updatedMaterials[index],
      ConvertedQuantity: value,
      ConvertedUnit: unit,
    };
    setVerificationForm(prev => ({ ...prev, rawMaterials: updatedMaterials }));
  };

  // Open material details dialog
  const openMaterialDetails = (index: number) => {
    setEditingMaterialIndex(index);
    setMaterialDetailsDialogOpen(true);
  };

  // Handle material details dialog close
  const handleMaterialDetailsClose = () => {
    setMaterialDetailsDialogOpen(false);
    setEditingMaterialIndex(null);
  };

  // Handle image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filePromises: Promise<string>[] = [];
      
      Array.from(e.target.files).forEach(file => {
        const filePromise = new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result === 'string') {
              resolve(reader.result);
            }
          };
          reader.readAsDataURL(file);
        });
        
        filePromises.push(filePromise);
      });
      
      Promise.all(filePromises).then(results => {
        setPreviewImages(prev => [...prev, ...results]);
        setVerificationForm(prev => ({
          ...prev,
          vendorBillImages: [...prev.vendorBillImages, ...results]
        }));
      });
    }
  };

  // Handle image removal
  const handleRemoveImage = (index: number) => {
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
    setVerificationForm(prev => ({
      ...prev,
      vendorBillImages: prev.vendorBillImages.filter((_, i) => i !== index)
    }));
  };

  // Reset form
  const resetForm = () => {
    setSelectedPurchaseOrder(null);
    setSearchPurchaseOrderId("");
    setVerificationForm({
      purchaseOrderId: "",
      rawMaterials: [],
      vendorBillId: "",
      vendorBillImages: [],
      securityComments: "",
      assignedTo: warehouseUsers.length > 0 ? warehouseUsers[0].UserId : "",
    });
    setPreviewImages([]);
    setError(null);
    setSuccess(null);
  };

  // Submit verification
  const submitVerification = async () => {
    if (!verificationForm.vendorBillId) {
      setError("Vendor Bill ID is required");
      return;
    }

    if (verificationForm.vendorBillImages.length === 0) {
      setError("At least one image of the vendor bill is required");
      return;
    }

    if (!verificationForm.assignedTo) {
      setError("You must assign this to a warehouse user");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Convert quantities back to base units
      const rawMaterialsForSubmission = verificationForm.rawMaterials.map(material => {
        let quantityInBaseUnit = material.ConvertedQuantity || 0;
        
        // If the converted unit is different from the base unit, convert back
        if (material.ConvertedUnit !== material.BaseUnit) {
          const baseCategory = unitCategoryMap[material.BaseUnit as UnitType];
          const convertedCategory = unitCategoryMap[material.ConvertedUnit as UnitType];
          
          if (baseCategory === convertedCategory) {
            // Direct conversion using standard conversions
            const baseConversionFactor = standardConversions[baseCategory][material.BaseUnit || "Pcs"] || 1;
            const convertedConversionFactor = standardConversions[convertedCategory][material.ConvertedUnit || "Pcs"] || 1;
            
            quantityInBaseUnit = (material.ConvertedQuantity || 0) * (convertedConversionFactor / baseConversionFactor);
          }
        }
        
        return {
          RawMaterialId: material.RawMaterialId,
          Quantity: quantityInBaseUnit,
          Price: material.Price,
        };
      });

      const response = await callApi<any>(
        apiEndpoints.NulookErpProcessPurchaseOrder,
        "POST",
        {
          operation: "accept_purchase_order",
          PurchaseOrderId: verificationForm.purchaseOrderId,
          raw_materials: rawMaterialsForSubmission,
          vendor_bill_id: verificationForm.vendorBillId,
          vendor_bill_images: verificationForm.vendorBillImages,
          security_comments: verificationForm.securityComments,
          AssignedTo: verificationForm.assignedTo,
        }
      );

      if (response.error) {
        throw new Error(response.error.message);
      }

      setSuccess("Purchase order verified successfully and sent to warehouse");
      resetForm();
    } catch (err) {
      setError("Failed to submit verification: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  // Format date from timestamp
  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Purchase Order Verification
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {!selectedPurchaseOrder ? (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search Purchase Order by ID
          </Typography>
          
          <Box component="form" onSubmit={(e) => { e.preventDefault(); searchPurchaseOrder(); }} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TextField
              fullWidth
              label="Purchase Order ID"
              value={searchPurchaseOrderId}
              onChange={handleSearchInputChange}
              sx={{ mr: 2 }}
              helperText="Enter the Purchase Order ID provided by the vendor"
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={searchPurchaseOrder}
              disabled={loading}
              type="submit"
            >
              {loading ? <CircularProgress size={24} /> : "Search"}
            </Button>
          </Box>
          
          <Alert severity="info">
            Security personnel can only verify purchase orders that have been created by the admin team and are in a "Pending" status.
          </Alert>
        </Paper>
      ) : (
        <Box>
          <Button 
            variant="outlined" 
            onClick={resetForm} 
            sx={{ mb: 2 }}
          >
            Back to Search
          </Button>
          
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Purchase Order Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">
                  <strong>PO ID:</strong> {selectedPurchaseOrder.PurchaseOrderId}
                </Typography>
                <Typography variant="body1">
                  <strong>Vendor:</strong> {selectedPurchaseOrder.VendorName}
                </Typography>
                <Typography variant="body1">
                  <strong>Vendor Phone:</strong> {selectedPurchaseOrder.VendorPhone}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">
                  <strong>Created At:</strong> {formatDate(selectedPurchaseOrder.CreatedAt)}
                </Typography>
                <Typography variant="body1">
                  <strong>Total Price:</strong> ₹{selectedPurchaseOrder.TotalPrice.toFixed(2)}
                </Typography>
                <Typography variant="body1">
                  <strong>Status:</strong>{" "}
                  <Chip
                    label={selectedPurchaseOrder.Status}
                    sx={{
                      bgcolor: statusColors[selectedPurchaseOrder.Status as keyof typeof statusColors] || "#757575",
                      color: "white",
                    }}
                    size="small"
                  />
                </Typography>
              </Grid>
            </Grid>
          </Paper>
          
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Raw Materials Verification
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Material</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Original Quantity</TableCell>
                    <TableCell>Verified Quantity</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {verificationForm.rawMaterials.map((material, index) => (
                    <TableRow key={material.RawMaterialId}>
                      <TableCell>{material.Name}</TableCell>
                      <TableCell>{material.Code}</TableCell>
                      <TableCell>
                        {material.Quantity} {material.BaseUnit}
                      </TableCell>
                      <TableCell>
                        {material.ConvertedQuantity} {material.ConvertedUnit}
                      </TableCell>
                      <TableCell>₹{material.Price.toFixed(2)}</TableCell>
                      <TableCell>
                        ₹{((material.ConvertedQuantity || 0) * material.Price).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => openMaterialDetails(index)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Verification Details
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vendor Bill ID"
                  name="vendorBillId"
                  value={verificationForm.vendorBillId}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Assign to Warehouse User</InputLabel>
                  <Select
                    value={verificationForm.assignedTo}
                    onChange={handleAssignedToChange}
                    label="Assign to Warehouse User"
                    required
                  >
                    {warehouseUsers.map((user) => (
                      <MenuItem key={user.UserId} value={user.UserId}>
                        {user.FullName} ({user.PhoneNumber})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Security Comments"
                  name="securityComments"
                  value={verificationForm.securityComments}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Upload Vendor Bill Images
                  <Tooltip title="Please upload clear images of the vendor bill. This is required.">
                    <IconButton size="small">
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Typography>
                
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                  {previewImages.map((image, index) => (
                    <Card key={index} sx={{ width: 200, position: "relative" }}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={image}
                        alt={`Vendor Bill ${index + 1}`}
                      />
                      <CardContent sx={{ p: 1 }}>
                        <Typography variant="caption">
                          Bill Image {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          sx={{ position: "absolute", top: 5, right: 5 }}
                          onClick={() => handleRemoveImage(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </CardContent>
                    </Card>
                  ))}
                  
                  <Button
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ height: 40, alignSelf: "flex-end" }}
                  >
                    Add Image
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                  />
                </Box>
                
                {previewImages.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    At least one vendor bill image is required
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Paper>
          
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleIcon />}
              onClick={submitVerification}
              disabled={loading || verificationForm.vendorBillImages.length === 0 || !verificationForm.vendorBillId}
            >
              {loading ? <CircularProgress size={24} /> : "Submit Verification"}
            </Button>
          </Box>
        </Box>
      )}
      
      {/* Material Details Dialog */}
      <Dialog
        open={materialDetailsDialogOpen}
        onClose={handleMaterialDetailsClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Edit Material Quantity
        </DialogTitle>
        <DialogContent>
          {editingMaterialIndex !== null && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6">
                {verificationForm.rawMaterials[editingMaterialIndex].Name}
              </Typography>
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {verificationForm.rawMaterials[editingMaterialIndex].Description}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">
                    <strong>Original Quantity:</strong>{" "}
                    {verificationForm.rawMaterials[editingMaterialIndex].Quantity}{" "}
                    {verificationForm.rawMaterials[editingMaterialIndex].BaseUnit}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">
                    <strong>Price per Unit:</strong>{" "}
                    ₹{verificationForm.rawMaterials[editingMaterialIndex].Price.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Verify Quantity (You can convert units if needed)
                </Typography>
                
                <UnitConverter
                  mode="convert"
                  baseUnit={verificationForm.rawMaterials[editingMaterialIndex].BaseUnit as UnitType}
                  baseValue={verificationForm.rawMaterials[editingMaterialIndex].Quantity}
                  unitConversions={verificationForm.rawMaterials[editingMaterialIndex].UnitConversions || []}
                  onChange={(value, unit) => 
                    handleMaterialChange(editingMaterialIndex, value, unit)
                  }
                />
              </Box>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                Tip: If the actual quantity is different from what was ordered, adjust it here.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMaterialDetailsClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrderVerification;